import { Link, Navigate, useParams } from "react-router-dom";
import { Button, Col, Overlay, Row, Table, Tooltip } from "react-bootstrap";
import {
  INDOOR_SECURITY_CAMERAS,
  OTHER,
  OUTDOOR_SECURITY_CAMERAS,
} from "../../products";
import { useRef, useState } from "react";
import style from "./styles.module.css";
import { useCart, isOutOfStock } from "../../contexts/Cart";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import localizedLink from "../../utils/localizedLink";
import productTitle from "../../utils/productTitle";
import useLanguage from "../../hooks/useLanguage";
import Countdown from "../../components/Countdown";
import Price from "../../components/Price";
import ProductLabel from "../../components/ProductLabel";
import Benefits from "../../components/Benefits";
import { ShoppingCart } from "lucide-react";
import Upsell from "./Upsell";

const PRODUCTS = [
  ...INDOOR_SECURITY_CAMERAS,
  ...OUTDOOR_SECURITY_CAMERAS,
  ...OTHER,
];

export default function Product() {
  const { language } = useLanguage();
  const { i18n } = useTranslation();
  const { addToCart } = useCart();
  const [showState, setShowState] = useState();
  const [targetState, setTargetState] = useState();
  const ref = useRef(null);

  const { productId } = useParams();
  const product = PRODUCTS.find((product) => product.id === productId);
  if (!product) return <Navigate to={localizedLink("/", language)} />;

  const handleClick = (event) => {
    setShowState(true);
    setTargetState(event.currentTarget);

    // Hide the tooltip after 3 seconds
    setTimeout(() => {
      setShowState(false);
    }, 2000);
  };

  return (
    <>
      <Benefits />

      <div className="mt-4 mt-sm-5">
        <Details
          lang={i18n.language}
          product={product}
          addToCart={addToCart}
          handleClick={handleClick}
          showState={showState}
          targetState={targetState}
          ref={ref}
        />
      </div>
    </>
  );
}

function Details({
  product,
  addToCart,
  handleClick,
  showState,
  targetState,
  ref,
}) {
  const { language } = useLanguage();
  return (
    <div>
      <Row>
        <Col md={6}>
          {product.oldPrice !== product.price && product.discountTitleKey && (
            <div className="bg-[#080a0f] text-white text-center px-2 py-1 rounded-xl">
              {t(product.discountTitleKey)}
              <Countdown targetDate={new Date('2025-11-28T23:59:59')} />
            </div>
          )}
          <picture
            className="relative mb-5 block overflow-hidden rounded-2xl bg-[#eaeaec] p-[10%]"
            onClick={() => window.scrollTo(0, 500)}
          >
            <img
              src={product.imageUrl}
              alt={productTitle(product, language)}
              className="w-full"
            />
            <ProductLabel
              label={product.label}
              className="absolute left-4 top-4"
            />
          </picture>
        </Col>
        <Col>
          <h2 className="text-2xl font-medium">{productTitle(product, language)}</h2>
          <Price product={product} size="lg" className="mt-3 mb-3" />
          {isOutOfStock(product) && (
            <p className="mb-2 text-red-800">{t("product.out-of-stock")}</p>
          )}
          <Link
            to={
              !isOutOfStock(product)
                ? localizedLink("/cart", language)
                : "#"
            }
          >
            <Button
              variant="dark"
              disabled={isOutOfStock(product)}
              className="mb-4 eufy-button"
              onClick={() => addToCart(product)}
            >
              {t("product.buy")}
            </Button>
          </Link>
          <Button
            id={`${product.id}-cart-button`}
            disabled={isOutOfStock(product)}
            variant="light"
            className="mb-4 ml-3 eufy-button eufy-button-secondary inline-flex items-center gap-2"
            onClick={(e) => {
              handleClick(e, product.id);
              addToCart(product);
            }}
          >
            <ShoppingCart className="h-[18px] w-[18px]" strokeWidth={2} />
            {t("product.add-to-cart")}
          </Button>
          <Overlay
            show={showState || false}
            target={targetState}
            placement="top"
            container={ref}
          >
            <Tooltip id={`tooltip-${product.id}`}>
              {t("product.product-added-to-cart")}
            </Tooltip>
          </Overlay>

          <ul className={style.unorderedList}>
            {product.shortDescription?.map(({ key, value }, index) => (
              <li className={style.listItem} key={index}>
                <strong className={style.strong}>{key[language]}</strong>{" "}
                {value[language]}
              </li>
            ))}
          </ul>

          <Upsell
            products={(product.upsell ?? [])
              .map((id) => PRODUCTS.find((item) => item.id === id))
              .filter((item) => item && !isOutOfStock(item))}
            addToCart={addToCart}
          />
        </Col>
      </Row>
      <Row>
        <Col></Col>
        <Col sm={12} md={10} lg={8}>
          {product.video && (
            <section className="mt-5">
              <h3 className="mb-3 text-xl">{t("product.video-review")}</h3>
              <iframe
                className="w-full"
                height="500"
                src={product.video}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </section>
          )}
          {product.specs?.length > 0 && (
            <section className="mt-5">
              <h3 className="mb-3 text-xl">{t("product.specs")}</h3>
              <Table striped="columns" className={style.specs}>
                <tbody>
                  {product.specs.map(({ key, value }, i) => (
                    <tr key={i.toString()}>
                      <td>{key[language]}</td>
                      <td>{value[language]}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          )}
        </Col>
        <Col></Col>
      </Row>
    </div>
  );
}
